import React, {createContext, useContext, useMemo} from 'react';
import {useIsMutating, useMutation, useQueryClient} from '@tanstack/react-query';
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import TalentCandidate from '../models/TalentCandidate';
import {useAuth} from './AuthProvider';
import {useAPI} from '../config/API';
import TalentPool from "../models/TalentPool";

const TalentPoolContext = createContext(null);

const LOOKUP = {
    "favorites": -1,
    "past-hires": -2,
    "bring-your-own-talent": -3,
    "blocked-talent": -10,
}

const GENERAL_DESCRIPTIONS = {
    '-1': 'This pool contains all talent that have been favorited with a heart',
    '-2': 'This pool contains all talent that have been hired in the past',
    '-3': 'This pool contains the talent you have invited to join Wripple',
    '-10': 'Talent added to this pool will be blocked from viewing your job postings and will not appear in your talent searches',
}

const SORT_OPTIONS_MEMBERS = {
    avg_review_rating: 'Rating',
    avg_hourly_rate: 'Average Bill Rate',
    min_hourly_rate: 'Bill Rate (low to high)',
    max_hourly_rate: 'Bill Rate (high to low)',
    first_name: 'Talent Name (A-Z)',
    job_title: 'Job Title',
    reviews_count: '# of Reviews',
};

const SORT_OPTIONS_TALENT = {
    best_match: 'Best Match (high to low)',
    min_bill_rate: 'Bill Rate (low to high)',
    max_bill_rate: 'Bill Rate (high to low)',
    experience: 'Total Experience (high to low)',
    key_skills: 'Key Skills (high to low)',
    industries: 'Industry Experience (high to low)',
    first_name: 'Talent Name (A-Z)',
};

const TalentPoolProvider = ({ children, ...props }) => {
    const queryClient = useQueryClient();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { auth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const isSelectingTalent = location.pathname.includes('add-talent');

    const TalentSortingOptions = useMemo(() => {
        let options = SORT_OPTIONS_TALENT;
        if (auth.clientOrg?.()?.location){
            options.proximity = 'Location (near to far)'
        }
        return options;
    }, [auth.clientOrg?.()]);

    //
    // CRUD TALENT POOL
    //
    const decodedSearchParams = useMemo(() => {
        let decoded = {'sort_by' :TB.first(TB.keys(SORT_OPTIONS_MEMBERS)), 'sort_dir': 'desc' };

        if (isSelectingTalent) {
            return decoded
        }
        decoded = [...searchParams.entries()].reduce((acc, [key, val]) => {
            try {
                return { ...acc, [key]: JSON.parse(val) };
            } catch {
                return { ...acc, [key]: val };
            }
        }, {});
        if (!decoded['sort_by']) {
            decoded['sort_by'] = TB.first(TB.keys(SORT_OPTIONS_MEMBERS))
            decoded['sort_dir'] = 'desc'
        }
        delete decoded.open
        return decoded
    }, [searchParams?.toString()]);

    const poolId = useMemo(() => {
        return LOOKUP[params.talentPoolId] ?? parseInt(params.talentPoolId);
    }, [params])


    const {
        data: talentPool,
        isFetching: isFetchTalentPoolDetailLoading,
        refetch,
        error
    } =
        useAPI(['fetchTalentPoolDetail', { clientId: auth.clientOrg?.()?.id, poolId, ...decodedSearchParams }], {
        enabled: !!poolId,
            // keepPreviousData: true,
        // select: data => TalentCandidate.makeMany(data.talent_users),
        // if invited , group by accepted and awaiting accepted

        select: data => {
            if (data.talent_invites) {
                const talent_invites = TalentCandidate.makeMany(data.talent_invites)
                const talent_users = TalentCandidate.makeMany(data.talent_users)
                return TalentPool.make({ ...data, talent_invites, talent_users });
            }
            return TalentPool.make({ ...data, talent_users: TalentCandidate.makeMany(data.talent_users) })
        },
        onSuccess: data => {
            if (data.settings) {
                let storage = {};
                TB.map(data.settings, (value, key) => {
                    try {
                        storage[key] = value;
                    }catch (e) {
                        console.log('parse issue', e);
                    }
                })
                console.log('onSuccessPERSISTING STORAGE', storage)
                TB.persistStoredBrowseFilters('talentPool-' + poolId, storage)
            }

            if (searchParams?.has('page') && parseInt(searchParams.get('page')) > 1 && !data?.talent_users?.length && data.total > 0) {
                onResetMemberData();
            }
        }
    })

    const onResetMemberData = () => {
        console.log('onResetMemberData')
        const params = new URLSearchParams(searchParams);
        if (params.has('page')) {
            params.delete('page');
        }
        if (params.has('search')) {
            params.delete('search');
        }
        return setSearchParams(params, { replace: true, preventScrollReset: true });
    }
    const onChangeSort = (key) => {
        const params = new URLSearchParams(searchParams);
        params.set('sort_by', key);
        if (params.has('page')) {
            params.delete('page');
        }
        return setSearchParams(params, { replace: true, preventScrollReset: true });
    }

    const onNameSearch = e => {
        if (e.key === 'Enter') {
            const params = new URLSearchParams(searchParams);
            if (e.target.value.length) {
                params.set('search', e.target.value);
            } else {
                params.delete('search');
            }
            return setSearchParams(params, { replace: true, preventScrollReset: true });
        }
    }

    //
    // Talent Pool Card
    //
    const bulkAddTalentToPoolMutation = useMutation(
        ['bulkAddTalentToPoolMutation'],
        (talentUserIds) => TalentCandidate.bulkAddToTalentToPool(auth.clientOrg?.()?.id, poolId, talentUserIds),
        {
            onSuccess: async (res, props) => {
                console.log('onSuccess', res, props)
                await TalentPool.resetTalentMemberDataForPool(parseInt(poolId));

                return TB.toast("Talent Added to pool", { type: 'success' });
            },
            onError: async (res, props) => {
                console.log('onError', res, props)
                return TB.showServerErrorToast();
            }
        }
    )
    const addTalentToPoolMutation = useMutation(
        ['addTalentToPoolMutation'],
        (talentUserId) => TalentCandidate.addTalentToPool(auth.clientOrg?.()?.id, poolId, talentUserId),
        {
            onSuccess: async (res, props) => {
                console.log('onSuccess', res, props)
                if (res) {
                    await TalentPool.resetTalentMemberDataForPool(parseInt(poolId));

                    // return TB.toast("Talent Added", { type: 'success' });
                }
            },
            onError: async (res, props) => {
                console.log('onError', res, props)
                return TB.showServerErrorToast();
            }
        }
    )

    const onAddTalentToPool = (talentUserId) => {
        return addTalentToPoolMutation.mutateAsync(talentUserId)
    }
    const onBulkAddTalentToPool = (talentUserId) => {
        return bulkAddTalentToPoolMutation.mutateAsync(talentUserId)
    }

    const removeTalentFromPoolMutation = useMutation(
        ['removeTalentFromPoolMutation'],
        talentUserId => {
            if (params?.talentPoolId === 'blocked-talent') {
                return TalentCandidate.removeTalentFromBlockedList(auth.clientOrg?.()?.id, talentUserId)
            }
            return TalentCandidate.removeTalentFromPool(auth.clientOrg?.()?.id, poolId, talentUserId)
        },
        {
            onSuccess: async (res, props) => {
                console.log('onSuccess', res)
                await TalentPool.resetTalentMemberDataForPool(parseInt(poolId));

                return TB.toast("Talent Removed", { type: 'success' });
            },
            onError: async (res, props) => {
                console.log('onError', res, props)
                return TB.showServerErrorToast();
            }
        }
    )

    const onRemoveTalentFromPool = async user => {
        const shouldDelete = await TB.alert(
            'error',
            `Remove ${user.displayName(auth)}?`,
            `Are you sure you want to remove ${user.displayName(auth)} from this talent pool? This cannot be undone`,
            [{ title: 'Yes, Remove' }, { title: 'Cancel' }],
        );

        if (shouldDelete) {
            return removeTalentFromPoolMutation.mutateAsync(user.referral_id || user.id)
        }
    }
    const onUpdateTalent = (updatedTalent) => {
        TalentPool.refreshTalentPoolMember(updatedTalent, poolId);
    }

    const talentPoolMutationKeys = [
        'addReviewMutation',
        'deleteReviewMutation',
        'removeTalentFromPoolMutation',
        'bulkAddTalentToPoolMutation',
        'addTalentToPoolMutation',
    ]
    const isDisabledByMutation = useIsMutating({predicate: (mutation) => {
        return talentPoolMutationKeys.includes(mutation.key)
    }})
    const value = {
        isLoading: isFetchTalentPoolDetailLoading,
        refetch,
        error,
        isDisabled: isDisabledByMutation,
        isAddingOrRemovingTalent: addTalentToPoolMutation.isLoading || bulkAddTalentToPoolMutation.isLoading || removeTalentFromPoolMutation.isLoading,
        searchCriteria: { ...decodedSearchParams, is_not_in_pool_id: TB.isInteger(parseInt(params.talentPoolId)) ? params.talentPoolId : undefined },
        talentPool,
        onChangeSort,
        onNameSearch,
        onResetMemberData,
        onAddTalentToPool,
        onBulkAddTalentToPool,
        onRemoveTalentFromPool,
        isSelectingTalent,
        SORT_OPTIONS_MEMBERS,
        SORT_OPTIONS_TALENT: TalentSortingOptions,
        onUpdateTalent
    }

    return (
        <TalentPoolContext.Provider value={value}>
            {children}
        </TalentPoolContext.Provider>
    )
}

const useTalentPool = () => {
    return useContext(TalentPoolContext)
}
export default TalentPoolProvider;
export {
    useTalentPool,
    TalentPoolContext,
};